import { Link } from 'react-router-dom';
import { 
  Building2, 
  Briefcase, 
  FlaskConical, 
  GraduationCap,
  ChevronRight,
  CheckCircle2
} from 'lucide-react';

export const Services = () => {
  const services = [
    {
      icon: Building2,
      title: 'Construction & Civil Engineering',
      description: 'Executing large-scale construction projects for government and private sector clients across India. Our expertise spans residential, commercial, and infrastructure projects with a focus on quality, safety, and timely delivery.',
      features: [
        'Large-scale infrastructure projects',
        'Commercial & residential construction',
        'Government tender projects',
        'Quality assurance & safety compliance',
      ],
      image: 'https://images.unsplash.com/photo-1765120232224-f0cd816d132e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxzaW1wbGUlMjByb2FkJTIwb3ZlcnBhc3MlMjBicmlkZ2UlMjBzaW5nbGUlMjBmbHlvdmVyfGVufDB8fHx8MTc3NTcxMzExN3ww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Briefcase,
      title: 'Engineering Consulting & Turnkey Projects',
      description: 'End-to-end project consulting, design, and turnkey execution for infrastructure projects. We provide comprehensive solutions from initial planning through final delivery, ensuring seamless project execution.',
      features: [
        'Feasibility studies & planning',
        'Project design & engineering',
        'End-to-end project management',
        'Turnkey delivery solutions',
      ],
      image: 'https://images.unsplash.com/photo-1762146828422-50a8bd416d3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBlbmdpbmVlcnMlMjBzaXRlJTIwYmx1ZXByaW50c3xlbnwwfHx8fDE3NzU2NDU2ODh8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: FlaskConical,
      title: 'Chemical Trading & Imports',
      description: 'Authorised dealing and import of specialised industrial chemicals including PET (Polyethylene Terephthalate) and Resin products. We serve manufacturing and industrial sectors with quality chemical supplies.',
      features: [
        'PET (Polyethylene Terephthalate)',
        'Industrial Resin products',
        'Specialised industrial chemicals',
        'Pan-India supply network',
      ],
      image: 'https://customer-assets.emergentagent.com/job_engineering-turnkey/artifacts/mp861f5f_13-pro_cat_img_2-Pet-resin-home.jpg',
    },
    {
      icon: GraduationCap,
      title: 'Vocational Training & Manpower Consulting',
      description: 'Skill development, vocational training, and manpower supply solutions for industry and government skilling programs. We bridge the gap between skilled workforce demand and talent development.',
      features: [
        'Industrial skill development',
        'Government skilling programs',
        'Manpower consulting services',
        'Workforce training solutions',
      ],
      image: 'https://images.unsplash.com/photo-1759763823587-c8bd07fca246?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwd29ya2VyJTIwdHJhaW5pbmclMjBmYWN0b3J5JTIwd29ya3Nob3AlMjBzYWZldHl8ZW58MHx8fHwxNzc1NzEyOTg0fDA&ixlib=rb-4.1.0&q=85',
    },
  ];

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section 
        data-testid="services-hero"
        className="relative py-20 md:py-32 bg-[#1A2B4A]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="overline mb-4">Our Services</p>
            <h1 
              data-testid="services-title"
              className="text-4xl sm:text-5xl font-bold text-white mb-6"
            >
              Comprehensive Solutions for<br />
              <span className="text-[#C9A84C]">Every Need</span>
            </h1>
            <p className="text-lg text-[#F5F5F0]/90">
              From construction to consulting, chemical trading to skill development — 
              we deliver excellence across diverse sectors.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section 
        data-testid="services-list"
        className="py-20 md:py-32 bg-[#F5F5F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-20 md:space-y-32">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-section-${index}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded bg-[#1A2B4A] flex items-center justify-center">
                      <service.icon className="w-7 h-7 text-[#C9A84C]" />
                    </div>
                    <span className="text-[#C9A84C] text-sm font-bold uppercase tracking-wider">
                      Service {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1A2B4A] mb-4">
                    {service.title}
                  </h2>
                  <p className="text-[#4A5568] leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, fIndex) => (
                      <li 
                        key={fIndex}
                        className="flex items-center gap-3 text-[#1A2B4A]"
                      >
                        <CheckCircle2 className="w-5 h-5 text-[#C9A84C] flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to="/contact"
                    data-testid={`service-cta-${index}`}
                    className="btn-outline-navy inline-flex items-center gap-2"
                  >
                    Enquire Now
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full rounded-sm shadow-xl"
                    />
                    <div className="absolute inset-0 bg-[#1A2B4A]/10 rounded-sm" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section 
        data-testid="process-section"
        className="py-20 bg-[#1A2B4A]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="overline mb-4">Our Process</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              How We Work
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Understanding your requirements and objectives' },
              { step: '02', title: 'Planning', desc: 'Developing comprehensive project plans' },
              { step: '03', title: 'Execution', desc: 'Delivering with quality and precision' },
              { step: '04', title: 'Delivery', desc: 'Ensuring complete client satisfaction' },
            ].map((item, index) => (
              <div 
                key={index}
                data-testid={`process-step-${index}`}
                className="text-center"
              >
                <div className="text-5xl font-bold text-[#C9A84C]/30 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-[#F5F5F0]/70 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        data-testid="services-cta"
        className="py-20 bg-[#F5F5F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1A2B4A] mb-4">
            Ready to Discuss Your Project?
          </h2>
          <p className="text-[#4A5568] mb-8 max-w-2xl mx-auto">
            Our team is ready to help you with your infrastructure and engineering needs.
            Get in touch today for a consultation.
          </p>
          <Link 
            to="/contact" 
            data-testid="services-contact-cta"
            className="btn-gold inline-flex items-center gap-2"
          >
            Contact Us
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
